/**
 * The degraded path (§6.5) — and its real ceilings.
 *
 * When there is no service worker (registration failed, the page is
 * uncontrolled, the worker is dead, or the browser is iOS Safari), a media
 * element cannot authenticate. The fallback is: `fetch()` with the bearer →
 * `blob` → `<a download>` → `URL.revokeObjectURL`.
 *
 * ⚠ THIS IS A MESSAGE, NOT A FEATURE, for most of this corpus. The blob is held
 * ENTIRELY IN MEMORY. The manuscript corpus skews large — 174,948 files over
 * 20 MB, 25.4 TB — so above the ceilings below the honest answer is the
 * open-in-Telegram link, exactly as telegram-tt refuses with *"Downloading
 * files bigger than 2GB is not supported in your browser"* rather than
 * pretending.
 */

import { getToken } from '@/api/client';
import { mediaStreamUrl } from '@/api/media';

/**
 * telegram-tt's own `MAX_BUFFER_SIZE`, adopted rather than invented: 2000 MB on
 * desktop, 512 MB on mobile. Chromium's in-memory blob budget is ~2 GB on x64
 * desktop and `total_physical_memory / 100` on Android — on a 4 GB phone that
 * is ~40 MB before it starts paging to disk, so the mobile number is already
 * generous.
 */
export const BLOB_CEILING_DESKTOP = 2000 * 1024 * 1024;
export const BLOB_CEILING_MOBILE = 512 * 1024 * 1024;

const isMobile = (): boolean =>
  typeof navigator !== 'undefined' &&
  /Android|iP(hone|ad|od)|Mobile|Opera Mini|IEMobile/.test(navigator.userAgent || '');

export const blobCeilingBytes = (): number =>
  isMobile() ? BLOB_CEILING_MOBILE : BLOB_CEILING_DESKTOP;

/**
 * Whether the degraded path can carry this file at all.
 *
 * A NULL size is ALLOWED through: `Media.file_size` is NULL for every photo
 * (14,055 manuscript rows — the insert path writes no size), and refusing
 * unknown sizes would refuse exactly the files the preview path exists for. The
 * real size is known only after the transfer, and the browser fails loudly if
 * it cannot hold it.
 */
export const fitsInBlob = (fileSize: number | null | undefined): boolean =>
  fileSize == null || fileSize <= blobCeilingBytes();

class MediaHttpError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'MediaHttpError';
    this.status = status;
  }
}

export const isMediaHttpError = (error: unknown): error is MediaHttpError =>
  error instanceof MediaHttpError;

/**
 * Fetch the bytes with the bearer attached BY HAND.
 *
 * Still no token in the URL (§6.3) — this is a header, on a request this code
 * issues itself. When a service worker IS live it passes this request straight
 * through, because it already carries an `Authorization` header and the worker
 * never overwrites one.
 */
const fetchMediaBlob = async (messageId: number, download: boolean): Promise<Blob> => {
  const token = getToken();
  const response = await fetch(mediaStreamUrl(messageId, download), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    cache: 'no-store',
  });
  if (!response.ok) {
    // 409 = not on disk, 401 = the token expired mid-preview, 503 = the
    // process is at `stream_max_concurrent`. The caller maps the code; the body
    // is deliberately not read here, since on `<img>`-shaped failures there may
    // not be one.
    throw new MediaHttpError(response.status, `stream failed: ${response.status}`);
  }
  return response.blob();
};

/**
 * An object URL for an inline preview. The caller MUST revoke it — an object
 * URL pins its blob in memory for the lifetime of the document, so a reviewer
 * scrolling a chat would otherwise accumulate every image they passed.
 */
export const createMediaObjectUrl = async (messageId: number): Promise<string> => {
  const blob = await fetchMediaBlob(messageId, false);
  return URL.createObjectURL(blob);
};

/**
 * Save to disk without a service worker.
 *
 * `dl=1` so the backend answers `Content-Disposition: attachment` and writes
 * the `media_download` audit row — the row is what makes an explicit save
 * distinguishable from a preview in `audit_log`, and the degraded path must not
 * be the one that goes unrecorded.
 */
export const downloadMediaViaBlob = async (
  messageId: number,
  fileName: string | null
): Promise<void> => {
  const blob = await fetchMediaBlob(messageId, true);
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    // The server's own `Content-Disposition` is unreachable from a blob, so the
    // name is re-supplied here. `mediaDisplayName` gives the same synthesised
    // `file-<telegram id>.<ext>` the card shows and the header carries, so the
    // saved file matches the bubble it came from either way.
    if (fileName) anchor.download = fileName;
    else anchor.download = `file-${messageId}`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    // One tick, so the click has started the save before the URL dies.
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }
};
